import { gql } from 'apollo-angular';

export const ASSET_FRAGMENT = gql`
    fragment Asset on Asset {
        id
        width
        height
        type
        preview
        source
        mimeType
        focalPoint {
            x
            y
        }
        customFields {
            videoUrl
            altText
        }
        imageUrl
    }
`;

export const NAVIGATION_ITEM_FRAGMENT = gql`
    fragment NavigationItem on NavigationMenuItem {
        label
        targetType
        pageSlug
        pageSection
        builtInPage
        customSlug
        websiteUrl
        targetProductId
        targetProductSlug
        targetCollectionId
        targetCollectionSlug
    }
`;

export const BUTTON_FRAGMENT = gql`
    fragment Button on ButtonData {
        enabled
        text
        textSize
        mobileTextSize
        textColor
        backgroundColor
        actionType
        selectedVariantId
        isVariantCustomizable
        navigationItem {
            ...NavigationItem
        }
        targetProduct {
            id
            name
            slug
        }
        targetCollection {
            id
            name
            slug
        }
    }
    ${NAVIGATION_ITEM_FRAGMENT}
`;

export const KEY_POINT_FRAGMENT = gql`
    fragment KeyPoint on KeyPointData {
        icon {
            ...Asset
        }
        title
        value
    }
    ${ASSET_FRAGMENT}
`;

export const BASIC_CARD_FRAGMENT = gql`
    fragment BasicCard on BasicCardData {
        asset {
            ...Asset
        }
        title
        text
    }
    ${ASSET_FRAGMENT}
`;

export const QA_FRAGMENT = gql`
    fragment QA on QaData {
        question
        answer
    }
`;

export const HERO_SECTION_FRAGMENT = gql`
    fragment HeroSection on HeroSectionData {
        layoutType
        title
        subtitle
        textColor
        textPositionX
        textPositionY
        mobileTextPositionX
        mobileTextPositionY
        textWidth
        titleFontSize
        subtitleFontSize
        mobileTitleFontSize
        mobileSubtitleFontSize
        shadowTransparencyValue
        buttonPosition
        buttonAlignment
        buttonLocationX
        buttonLocationY
        buttonPositionMobile
        buttonAlignmentMobile
        buttonLocationXMobile
        buttonLocationYMobile
        button {
            ...Button
        }
        medias {
            ...Asset
        }
        mobileMedias {
            ...Asset
        }
    }
    ${BUTTON_FRAGMENT}
    ${ASSET_FRAGMENT}
`;

export const EXTERNAL_HTML_CONTENT_FRAGMENT = gql`
    fragment ExternalHtmlContent on ExternalHtmlContentData {
        htmlContent
        medias {
            ...Asset
        }
        mobileMedias {
            ...Asset
        }
    }
    ${ASSET_FRAGMENT}
`;

export const MARKETING_BANNER_FRAGMENT = gql`
    fragment MarketingBanner on MarketingBannerData {
        type
        mediaHeight
        mediaRoundedCorners
        textBarStyle
        leftRightPartRatio
        mediaTextGap
        shadowTransparencyValue
        autoSlideDelay
        layoutStyle
        backgroundColor
        textColor
        title
        text
        titleFontSize
        textFontSize
        textAlignment
        textWidth
        textIndentation
        titleIndentation
        secColTitle
        secColText
        secColTitleFontSize
        secColTextFontSize
        secColTextAlignment
        secColTextWidth
        secColTitleIndentation
        secColTextIndentation
        buttonPosition
        buttonAlignment
        buttonLocationX
        buttonLocationY
        buttonPositionMobile
        buttonAlignmentMobile
        buttonLocationXMobile
        buttonLocationYMobile
        button {
            ...Button
        }
        medias {
            ...Asset
        }
        mobileMedias {
            ...Asset
        }
        mobileMediaPosition
        mobileMediaStyle
        backgroundMedia {
            ...Asset
        }
        backgroundMobileMedia {
            ...Asset
        }
    }
    ${BUTTON_FRAGMENT}
    ${ASSET_FRAGMENT}
`;

export const PRODUCT_REVIEW_FRAGMENT = gql`
    fragment ProductReview on ProductReview {
        id
        updatedAt
        summary
        body
        rating
        authorName
        featuredAsset {
            ...Asset
        }
    }
    ${ASSET_FRAGMENT}
`

export const LOGO_SECTION_FRAGMENT = gql`
    fragment LogoSection on LogoSectionData {
        normalLogo {
            id
            width
            height
            preview
            focalPoint {
                x
                y
            }
            imageUrl
        }
        footerLogo {
            id
            width
            height
            preview
            focalPoint {
                x
                y
            }
            imageUrl
        }
        smallLogo {
            id
            width
            height
            preview
            focalPoint {
                x
                y
            }
            imageUrl
        }
        nameLogo {
            id
            width
            height
            preview
            focalPoint {
                x
                y
            }
            imageUrl
        }
        webclipIcon {
            id
            source
            imageUrl
        }
        browserIcon {
            id
            source
        }
    }
`;

export const VARIANT_CUSTOM_FIELDS_FRAGMENT = gql`
    fragment ProductVariantCustomFields on ProductVariantCustomFields {
        MSRP
    }
`;

export const PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT = gql`
    fragment ProductCustomizationOption on ProductCustomizationOptionData {
        id
        name
        code
        type
        previewAsset {
            ...Asset
        }
        printTemplate {
            label
            description
            minWidth
            minHeight
            canvasWidth
            canvasHeight
            backgroundImageDisplayType
            backgroundImages {
                label
                asset {
                    ...Asset
                }
            }
            imageFrames {
                name
                label
                labelColor
                labelFontSize
                positionX
                positionY
                sizeX
                sizeY
                textFrameGroups {
                    name
                    allowedFonts
                    allowedColors {
                        hex
                        name
                    }
                    defaultFont
                    defaultColor
                    autoFit
                    fontSizeOptions
                    defaultFontSize
                    frames {
                        name
                        label
                        maxLength
                        maxLines
                        required
                        textAlign
                    }
                }
            }
        }
        textFrames {
            label
            description
            textFrameGroups {
                name
                allowedFonts
                allowedColors {
                    hex
                    name
                }
                defaultFont
                defaultColor
                autoFit
                fontSizeOptions
                defaultFontSize
                frames {
                    name
                    label
                    maxLength
                    maxLines
                    required
                    textAlign
                }
            }
        }
        message {
            label
            description
            required
        }
        paidOption {
            label
            description
            required
            showImage
            displayType
            options {
                key
                label
                description
                price
                asset {
                    ...Asset
                }
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const CART_FRAGMENT = gql`
    fragment Cart on Order {
        id
        code
        state
        active
        createdAt
        updatedAt
        couponCodes
        orderPlacedAt
        customer {
            id
            firstName
            emailAddress
            customFields {
                nickname
            }
        }
        shippingAddress {
            fullName
            country
            countryCode
            postalCode
        }
        lines {
            id
            featuredAsset {
                id
                preview
                focalPoint {
                    x
                    y
                }
                imageUrl
            }
            customizationPreviewUrl
            customizationOptionName
            orderedCustomizableAssets {
                type
                name
                text
                config
                amount
                productCustomizationOption {
                    id
                    name
                }
            }
            unitPrice
            unitPriceWithTax
            quantity
            linePriceWithTax
            discountedLinePriceWithTax
            productVariant {
                id
                name
                sku
                price
                featuredAsset {
                    id
                    preview
                    focalPoint {
                        x
                        y
                    }
                    imageUrl
                }
                options {
                    name
                }
                product {
                    id
                    name
                    slug
                    featuredAsset {
                        id
                        preview
                        focalPoint {
                            x
                            y
                        }
                        imageUrl
                    }
                    channels {
                        id
                        code
                        seller {
                            id
                            name
                        }
                    }
                }
                customFields {
                    MSRP
                }
            }
            discounts {
                amount
                amountWithTax
                description
                adjustmentSource
                type
            }
        }
        totalQuantity
        subTotal
        subTotalWithTax
        total
        totalWithTax
        shipping
        shippingWithTax
        shippingLines {
            priceWithTax
            shippingMethod {
                id
                code
                name
                description
            }
        }
        discounts {
            amount
            amountWithTax
            description
            adjustmentSource
            type
        }
        customFields {
            message
            utmInfo {
                utmId
                utmSource
                utmMedium
                utmCampaign
                utmTerm
                utmContent

            }
        }
    }
`;

export const COUNTRY_FRAGMENT = gql`
    fragment Country on Country {
        id
        code
        name
        enabled
    }
`;

export const ORDER_ADDRESS_FRAGMENT = gql`
    fragment OrderAddress on OrderAddress {
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        country
        countryCode
        phoneNumber
    }
`;

export const CHANNEL_RETURN_ADDRESS_FRAGMENT = gql`
    fragment ReturnAddress on ChannelReturnAddressStruct {
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        countryCode
        phoneNumber
    }
`;

export const COLLECTION_FRAGMENT = gql`
    fragment Collection on Collection {
        id
        name
        slug
        description
        customFields {
            itemOrderData
        }
        parent {
            id
            name
            slug
        }
        featuredAsset {
            ...Asset
        }
        storeSite {
            id
            name
            identifier
            sellerChannel {
                id
            }
            colorConfig {
                mainColor
                mainTextColor
                secondaryColor
                secondaryTextColor
                headerBackgroundColor
                headerFontColor
                footerBackgroundColor
                footerFontColor
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const FACET_VALUE_FRAGMENT = gql`
    fragment FacetValue on FacetValue {
        id
        name
        code
        customFields {
            priority
        }
        facet {
            id
            name
            code
        }
    }
`;

export const FACET_FRAGMENT = gql`
    fragment Facet on Facet {
        id
        name
        code
        values {
            ...FacetValue
        }
    }
    ${FACET_VALUE_FRAGMENT}
`;

export const WEB_PRESENCE_FRAGMENT = gql`
    fragment WebPresence on WebPresenceConfigData {
        useBuiltInSeo
        title
        description
        metaAsset {
            ...Asset
        }
        openGraphTitleSameAsSeoTitle
        openGraphDescriptionSameAsSeoDescription
        openGraphTitle
        openGraphDescription
        h1Source
        h1Text
        h1StyleMode
        h1FontSize
        h1MobileFontSize
        h1Color
        h1Alignment
    }
    ${ASSET_FRAGMENT}
`;

export const EMAIL_SUBSCRIPTION_FRAGMENT = gql`
    fragment EmailSubscriptionData on EmailSubscriptionData {
        type
        backgroundColor
        backgroundImage {
            ...Asset
        }
        shadowTransparencyValue
        omnisendFormId
    }
    ${ASSET_FRAGMENT}
`;

export const COLLECTION_GROUP_FRAGMENT = gql`
    fragment CollectionGroup on CollectionGroupData {
        groupName
        description
        backgroundAsset {
            ...Asset
        }
        featuredAssets {
            ...Asset
        }
        collections {
            id
            name
            slug
            description
            featuredAsset {
                id
                preview
                focalPoint {
                    x
                    y
                }
                customFields {
                    altText
                }
                imageUrl
            }
        }
    }
    ${ASSET_FRAGMENT}
`;

export const FEATURED_COLLECTION_FRAGMENT = gql`
    fragment FeaturedCollection on FeaturedCollectionData {
        collection {
            id
            name
            slug
            customFields {
                itemOrderData
            }
        }
        displayType
        productCardType
        cardsPerRow
        mobileCardsPerRow
        productCardShape
        displayFields
        productCardRoundedCorners
        buttonText
        buttonColorMode
        buttonBgColor
        buttonTextColor
    }
`;

export const KEY_POINT_LIST_FRAGMENT = gql`
    fragment KeyPointList on KeyPointListData {
        iconSize
        titleFontSize
        subtitleFontSize
        textFontSize
        titleAlignment
        textAlignment
        backgroundColor
        textColor
        keyPointsLayoutType
        mobileKeyPointsLayoutType
        keyPoints {
            ...KeyPoint
        }
    }
    ${KEY_POINT_FRAGMENT}
`;

export const CARD_CAROUSEL_BANNER_FRAGMENT = gql`
    fragment CardCarouselBanner on CardCarouselBannerData {
        titleFontSize
        subtitleFontSize
        textFontSize
        titleAlignment
        textAlignment
        basicCardShape
        slideNumber
        mobileStyle
        backgroundColor
        textColor
        mediaRoundedCorners
        basicCards {
            ...BasicCard
        }
    }
    ${BASIC_CARD_FRAGMENT}
`;

export const FAQ_LIST_FRAGMENT = gql`
    fragment FaqList on FaqListData {
        defaultDisplayCount
        qaList {
            ...QA
        }
    }
    ${QA_FRAGMENT}
`;

export const PRODUCT_LISTING_FRAGMENT = gql`
    fragment ProductListing on ProductListingData {
        productListingType
        collection {
            id
            name
            slug
        }
        facetValues {
            ...FacetValue
        }
    }
    ${FACET_VALUE_FRAGMENT}
`;

export const PAGE_PRODUCT_FRAGMENT = gql`
    fragment PageProduct on Product {
        id
        name
        slug
        description
        variants {
            id
            name
            options {
                id
                code
                name
                groupId
                group {
                    code
                }
            }
            currencyCode
            price
            priceWithTax
            sku
            stockLevel
            featuredAsset {
                ...Asset
            }
            assets {
                ...Asset
            }
            customFields {
                MSRP
                customizationTemplateDisplaySize
                gtin
                mpn
                variantSubtitle
            }
            customizationOptions {
                ...ProductCustomizationOption
            }
        }
        optionGroups {
            id
            code
            name
            customFields {
                displayType
                priority
            }
            options {
                id
                code
                name
                customFields {
                    Priority
                }
            }
        }
        customFields {
            OGDescription
            OGImage {
                id
                preview
                imageUrl
            }
            ProductSubtitle
            ProductFeatures
            ProductDisclaimer
            reviewRating
            reviewCount
            salesNumber
            salesStatement
            featuredReview {
                id
                createdAt
                rating
                authorName
                summary
                body
                featuredAsset {
                    ...Asset
                }
            }
            customizationTemplateDisplaySize
            productKeySellingPointList
            productCardBackgroundColor
            defaultVariant {
                id
            }
            displayFeaturedReview
        }
        featuredAsset {
            ...Asset
        }
        assets {
            ...Asset
        }
        customizationOptions {
            ...ProductCustomizationOption
        }
    }
    ${ASSET_FRAGMENT}
    ${PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT}
`;

export const PAGE_VARIANT_FRAGMENT = gql`
    fragment PageVariant on ProductVariant {
        id
        name
        options {
            id
            code
            name
            groupId
            group {
                code
            }
        }
        currencyCode
        price
        priceWithTax
        sku
        stockLevel
        featuredAsset {
            ...Asset
        }
        assets {
            ...Asset
        }
        customFields {
            MSRP
            customizationTemplateDisplaySize
        }
        product {
            ...PageProduct
        }
    }
    ${PRODUCT_CUSTOMIZATION_OPTION_FRAGMENT}
    ${ASSET_FRAGMENT}
`;

export const COLLECTION_CAROUSEL_FRAGMENT = gql`
    fragment CollectionCarousel on CollectionCarouselData {
        textAlignment
        mobileStyle
        collections {
            ...Collection
        }
    }
    ${COLLECTION_FRAGMENT}
`;

export const ABOUT_FORM_FRAGMENT = gql`
    fragment AboutForm on AboutFormData {
        text
        citation
        featuredAsset {
            id
            preview
            focalPoint {
                x
                y
            }
            customFields {
                altText
            }
            imageUrl
        }
    }
`;

export const CONTACT_FORM_FRAGMENT = gql`
    fragment ContactForm on ContactFormData {
        contactFormType
        email
        featuredAsset {
            id
            preview
            focalPoint {
                x
                y
            }
            customFields {
                altText
            }
            imageUrl
        }
    }
`;

export const PRODUCT_INFO_FRAGMENT = gql`
    fragment PageInfoData on ProductInfoData {
        product {
            ...PageProduct
        }
        productVariant {
            ...PageVariant
        }
    }
    ${PAGE_PRODUCT_FRAGMENT}
    ${PAGE_VARIANT_FRAGMENT}
`;

export const SIMPLE_PRODUCT_INFO_FRAGMENT = gql`
    fragment SimpleProductInfo on SimpleProductInfoData {
        productVariant {
            ...PageVariant
        }
        displayThumbnails
        displayFeaturedReview
        displayProductSubtitle
        displaySku
        displayReviewInfo
        displayPaymentCapabilities
        displayProductOptions
        displayProductDescription
        displayAddToCart
        displayProductKeyPoints
        displayProductFeatures
        displayProductPageEntry
    }
    ${PAGE_VARIANT_FRAGMENT}
`;

export const TOP_REVIEWS_FRAGMENT = gql`
    fragment TopReviews on TopReviewsData {
        reviews {
            ...ProductReview
        }
    }
    ${PRODUCT_REVIEW_FRAGMENT}
`;

export const PRODUCT_REVIEWS_FRAGMENT = gql`
    fragment ProductReviews on ProductReviewsData {
        backgroundColor
        product {
            id
            customFields {
                reviewRating
                reviewCount
                featuredReview {
                    ...ProductReview
                }
            }
        }
    }
    ${PRODUCT_REVIEW_FRAGMENT}
`;

export const LAYOUT_OPTION_FRAGMENT = gql`
    fragment LayoutOption on LayoutOptionData {
        width
        mobileWidth
        topMargin
        bottomMargin
        mobileTopMargin
        mobileBottomMargin
        horizontalPadding
        topPadding
        bottomPadding
        mobileHorizontalPadding
        mobileTopPadding
        mobileBottomPadding
        sectionRoundedCorners
        mobileSectionRoundedCorners
        pageFullWidth
        sectionMaxHeight
    }
`;

export const PAGE_COMPONENT_FRAGMENT = gql`
    fragment PageComponent on PageComponentData {
        type
        sectionId
        titleMode
        title
        subtitle
        titleUnderline
        layoutOption {
            ...LayoutOption
        }
        content {
          __typename
          ... on ProductListingData { ...ProductListing }
          ... on MarketingBannerData { ...MarketingBanner }
          ... on HeroSectionData { ...HeroSection }
          ... on KeyPointListData { ...KeyPointList }
          ... on CardCarouselBannerData { ...CardCarouselBanner }
          ... on ExternalHtmlContentData { ...ExternalHtmlContent }
          ... on FaqListData { ...FaqList }
          ... on FeaturedCollectionData { ...FeaturedCollection }
          ... on CollectionGroupData { ...CollectionGroup }
          ... on EmailSubscriptionData { ...EmailSubscriptionData }
          ... on TopReviewsData { ...TopReviews }
          ... on SimpleProductInfoData { ...SimpleProductInfo }
          ... on ProductInfoData { ...PageInfoData }
          ... on ProductReviewsData { ...ProductReviews }
          ... on CollectionCarouselData { ...CollectionCarousel }
          ... on ContactFormData { ...ContactForm }
          ... on AboutFormData { ...AboutForm }
        }
    }
    ${LAYOUT_OPTION_FRAGMENT}
    ${MARKETING_BANNER_FRAGMENT}
    ${HERO_SECTION_FRAGMENT}
    ${KEY_POINT_LIST_FRAGMENT}
    ${CARD_CAROUSEL_BANNER_FRAGMENT}
    ${EXTERNAL_HTML_CONTENT_FRAGMENT}
    ${FAQ_LIST_FRAGMENT}
    ${FEATURED_COLLECTION_FRAGMENT}
    ${COLLECTION_GROUP_FRAGMENT}
    ${PRODUCT_REVIEWS_FRAGMENT}
    ${EMAIL_SUBSCRIPTION_FRAGMENT}
    ${PRODUCT_INFO_FRAGMENT}
    ${SIMPLE_PRODUCT_INFO_FRAGMENT}
    ${PRODUCT_LISTING_FRAGMENT}
    ${COLLECTION_CAROUSEL_FRAGMENT}
    ${ABOUT_FORM_FRAGMENT}
    ${CONTACT_FORM_FRAGMENT}
    ${TOP_REVIEWS_FRAGMENT}
`;

export const FULFILLMENT_FRAGMENT = gql`
    fragment Fulfillment on Fulfillment {
        id
        state
        createdAt
        updatedAt
        method
        lines {
            orderLineId
            quantity
        }
        trackingCode
        customFields {
            trackingUrl
        }
    }
`;

export const ADDRESS_FRAGMENT = gql`
    fragment Address on Address {
        id
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        country {
            id
            code
            name
        }
        phoneNumber
        defaultShippingAddress
        defaultBillingAddress
    }
`;

export const ERROR_RESULT_FRAGMENT = gql`
    fragment ErrorResult on ErrorResult {
        errorCode
        message
    }
`;

export const PAYMENT_INTENT_FRAGMENT = gql`
    fragment PaymentIntentResponse on PaymentIntentResponse {
        paymentIntentId
        clientSecret
        showRecaptcha
        stripeAccount
        status
    }
`;

export const STRIPE_CAPABILITY_FRAGMENT = gql`
    fragment StripeCapability on StripeCapability {
        name
        status
    }
`;
